/**
 * SMS body rules — the client-side mirror of the comms service's
 * `core/sms_text.py` (sm4rt_emailer).
 *
 * Keep the two in step: the composer's counter is only useful if it agrees
 * with what the server will accept. The server re-validates regardless — this
 * exists so the operator sees the cost and the blocked characters as they type
 * rather than after a failed send.
 *
 * A body made only of GSM 03.38 characters packs 7 bits per character: 160 in
 * a single message, 153 per part once split. One character outside that set —
 * an emoji, a smart quote pasted from Word — forces the whole message to UCS-2
 * and halves capacity, so those are rejected rather than silently paid for.
 */

/** GSM 03.38 basic character set — one septet each. */
const GSM7_BASIC = new Set(
  '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?' +
    '¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà',
);

/** GSM 03.38 extension table — two septets each (escape + character). */
const GSM7_EXTENDED = new Set('^{}\\[~]|€');

/** TouchSMS hard cap on a single message body. */
export const MAX_BODY_LENGTH = 1600;

const SINGLE_PART_SEPTETS = 160;
const MULTI_PART_SEPTETS = 153;

export interface BodyStats {
  /** Characters as typed. */
  length: number;
  /** GSM-7 septets — extended characters count twice. */
  septets: number;
  /** SMS parts, i.e. credits per recipient. */
  parts: number;
  /** Distinct characters that can't be sent, in order of first use. */
  unsupported: string[];
  /** Characters left before the message costs another credit. */
  remainingInPart: number;
}

const septetCost = (ch: string): number => (GSM7_EXTENDED.has(ch) ? 2 : 1);

/** Distinct non-GSM-7 characters in `body`, in order of first use. */
export function findUnsupportedCharacters(body: string): string[] {
  const seen = new Set<string>();
  const unsupported: string[] = [];
  for (const ch of body) {
    if (GSM7_BASIC.has(ch) || GSM7_EXTENDED.has(ch) || seen.has(ch)) continue;
    seen.add(ch);
    unsupported.push(ch);
  }
  return unsupported;
}

/**
 * Measure a body: length, septets, parts and how much room is left in the
 * current part. Extended characters are never split across a part boundary,
 * so parts are packed rather than divided arithmetically.
 */
export function measureBody(body: string): BodyStats {
  const unsupported = findUnsupportedCharacters(body);
  let septets = 0;
  for (const ch of body) septets += septetCost(ch);

  if (!body) {
    return { length: 0, septets: 0, parts: 0, unsupported, remainingInPart: SINGLE_PART_SEPTETS };
  }

  if (septets <= SINGLE_PART_SEPTETS) {
    return {
      length: body.length,
      septets,
      parts: 1,
      unsupported,
      remainingInPart: SINGLE_PART_SEPTETS - septets,
    };
  }

  let parts = 1;
  let used = 0;
  for (const ch of body) {
    const cost = septetCost(ch);
    if (used + cost > MULTI_PART_SEPTETS) {
      parts += 1;
      used = 0;
    }
    used += cost;
  }

  return {
    length: body.length,
    septets,
    parts,
    unsupported,
    remainingInPart: MULTI_PART_SEPTETS - used,
  };
}

/** Human-readable reason the body can't be sent, or null when it's fine. */
export function bodyError(body: string): string | null {
  if (!body.trim()) return null; // empty is "not ready", not an error to show
  if (body.length > MAX_BODY_LENGTH) {
    return `Message is ${body.length} characters — the limit is ${MAX_BODY_LENGTH}.`;
  }
  const unsupported = findUnsupportedCharacters(body);
  if (unsupported.length) {
    const shown = unsupported.slice(0, 8).map(ch => `"${ch}"`).join(' ');
    const more = unsupported.length > 8 ? ` (and ${unsupported.length - 8} more)` : '';
    return `Unsupported character${unsupported.length > 1 ? 's' : ''}: ${shown}${more}. Emoji and smart quotes aren't supported — use plain text.`;
  }
  return null;
}
